// Column definitions and persistence for the contracts list. Scoped to
// ContractsPage on purpose: not a generic table framework.

export type ContractColumnId =
  | 'contract'
  | 'type'
  | 'status'
  | 'conformity'
  | 'parties'
  | 'pii'
  | 'asset'
  | 'period'

export interface ContractColumn {
  id: ContractColumnId
  // i18n key in the `contracts` namespace (the `common` namespace for PII).
  labelKey: string
  labelNs?: string
  defaultVisible: boolean
  // Relative width; the visible columns' widths are normalised to 100%.
  width: number
  // The contract column is always shown and has no toggle.
  required?: boolean
}

export const CONTRACT_COLUMNS: ContractColumn[] = [
  { id: 'contract', labelKey: 'list.column_contract', defaultVisible: true, width: 28, required: true },
  { id: 'type', labelKey: 'list.column_type', defaultVisible: false, width: 13 },
  { id: 'status', labelKey: 'list.column_status', defaultVisible: true, width: 12 },
  { id: 'conformity', labelKey: 'list.column_conformity', defaultVisible: false, width: 13 },
  { id: 'parties', labelKey: 'list.column_parties', defaultVisible: false, width: 20 },
  { id: 'pii', labelKey: 'pii.filter.label', labelNs: 'common', defaultVisible: false, width: 12 },
  { id: 'asset', labelKey: 'list.column_asset', defaultVisible: true, width: 20 },
  { id: 'period', labelKey: 'list.column_period', defaultVisible: true, width: 16 },
]

export const CONTRACT_COLUMNS_STORAGE_KEY = 'contracts.columns'

export function defaultContractColumns(): ContractColumnId[] {
  return CONTRACT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id)
}

// Parses a stored selection. Unknown ids are dropped, the required Contract
// column is always kept, anything unparseable falls back to the defaults.
export function parseContractColumns(raw: string | null | undefined): ContractColumnId[] {
  if (!raw) return defaultContractColumns()
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return defaultContractColumns()
  }
  if (!Array.isArray(parsed)) return defaultContractColumns()
  const known = new Set<string>(CONTRACT_COLUMNS.map((c) => c.id))
  const picked = new Set(parsed.filter((id): id is string => typeof id === 'string' && known.has(id)))
  return CONTRACT_COLUMNS.filter((c) => c.required || picked.has(c.id)).map((c) => c.id)
}

export function serializeContractColumns(ids: Iterable<ContractColumnId>): string {
  const set = new Set(ids)
  return JSON.stringify(CONTRACT_COLUMNS.filter((c) => c.required || set.has(c.id)).map((c) => c.id))
}

export function loadContractColumns(): ContractColumnId[] {
  try {
    return parseContractColumns(window.localStorage.getItem(CONTRACT_COLUMNS_STORAGE_KEY))
  } catch {
    return defaultContractColumns()
  }
}

export function saveContractColumns(ids: ContractColumnId[]): void {
  try {
    window.localStorage.setItem(CONTRACT_COLUMNS_STORAGE_KEY, serializeContractColumns(ids))
  } catch {
    // Private mode or blocked storage: the choice just doesn't persist.
  }
}

export function clearContractColumns(): void {
  try {
    window.localStorage.removeItem(CONTRACT_COLUMNS_STORAGE_KEY)
  } catch {
    // ignore
  }
}
